/**
 * molliT1.js — T1 миокарда по серии MOLLI. [10.19.22]
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ МОДУЛЬ. В relaxometry.js в разделе «чего здесь нет» с 10.19.14
 * стоит признание: MOLLI считается ДРУГОЙ моделью, и той, что там есть,
 * MOLLI-серию считать нельзя — T1 выйдет заниженным. Это ровно тот пробел,
 * который здесь и закрывается. Модель другая не по вкусу, а по устройству
 * последовательности, и путать их нечем: у них разное число параметров.
 *
 * МОДЕЛЬ. MOLLI снимает по одному кадру на каждый R-R интервал после инверсии,
 * то есть само считывание всё время сбивает восстановление. Экспонента остаётся
 * экспонентой, но с ТРЕМЯ параметрами и с «кажущимся» временем T1*:
 *
 *     S(TI) = A − B · exp(−TI / T1*)
 *
 * A — асимптотика (та, к которой сигнал приходит ПРИ ЭТОМ считывании, она ниже
 * равновесной), B — размах, T1* — кажущееся время. Истинное время получается
 * поправкой Лука–Локера:
 *
 *     T1 = T1* · (B/A − 1)
 *
 * При идеальной инверсии и без возмущения от считывания B/A = 2 и поправка
 * ничего не меняет; считывание опускает A, отношение растёт (обычно 2,1…2,4), и
 * поправка ПОДНИМАЕТ ответ. Пропустить её — значит систематически занизить T1
 * на десятки процентов: на здешнем фантоме с B/A = 2,2 это ровно 16,7 %, и это
 * закреплено контролем в tests/molli_test.mjs, а не оставлено на веру.
 *
 * ПОЛЯРНОСТЬ. Реконструктор отдаёт МОДУЛЬ, а S(TI) до точки нуля отрицательна.
 * Считать по модулю как есть нельзя: кривая «отражается» вверх, и подгонка
 * уезжает. Знак восстанавливается перебором: точки отсортированы по TI, первые
 * k из них берутся с минусом, k пробегает все возможные значения, выигрывает то,
 * что даёт наименьшую невязку. Перебор, а не «поиск минимума и всё, что до
 * него — минус»: минимум по модулю лежит МЕЖДУ кадрами, и на шумных данных
 * ближайший к нулю кадр не обязан быть последним отрицательным.
 *
 * ПОЧЕМУ ПОДГОНКА ДЕШЁВАЯ. При фиксированном T1* модель линейна по A и B:
 * f_i = exp(−TI_i/T1*) считается один раз на узел сетки (от вокселя не зависит),
 * а оптимальные A и B находятся из системы 2×2 в явном виде. Поэтому перебор по
 * логарифмической сетке T1* с двумя проходами уточнения стоит терпимо даже на
 * кадре 256×256, и никакого градиентного спуска с его капризами не нужно.
 *
 * ЧЕГО ЗДЕСЬ НЕТ — прямо, чтобы карту не приняли за клиническую:
 *
 *   · Нет коррекции движения между кадрами. MOLLI снимается 11–17 секунд на
 *     задержке дыхания; если пациент двигался, карта смазана по границам, и
 *     среднее по стенке будет неверным. Признак движения здесь не считается.
 *   · MOLLI сам по себе занижает T1 относительно эталонной инверсии-восстановления
 *     и ЗАВИСИТ ОТ ЧАСТОТЫ ПУЛЬСА: при тахикардии восстановление между инверсиями
 *     неполное. Поправка Лука–Локера этого не лечит — она лечит только влияние
 *     считывания.
 *   · ShMOLLI (укороченный вариант) требует УСЛОВНОЙ подгонки: длинные T1
 *     считаются по первым пяти кадрам, остальные — по всем. Здесь этого нет;
 *     ShMOLLI-серия посчитается обычной трёхпараметрической моделью, и на
 *     длинных T1 (кровь, отёк) ответ будет смещён. Модуль об этом говорит.
 *   · TI берутся из тегов как есть. MOLLI пишет ЭФФЕКТИВНОЕ время инверсии для
 *     каждого кадра; если в серии стоят номинальные значения, ошибка уедет в
 *     ответ, и увидеть это по карте нельзя.
 *   · Нет разделения кровь/миокард и нет маски камеры: среднее по всему срезу —
 *     не «T1 миокарда».
 *
 * Воксель, который не подошёл под модель, получает NaN и попадает в счётчик
 * отказов С ПРИЧИНОЙ. Ноль миллисекунд был бы ложью, которую невозможно
 * заметить на цветной картинке.
 */

/** Причины отказа — счётчики возвращаются наружу, чтобы отказ можно было объяснить. */
export const ПРИЧИНЫ = Object.freeze({
  СЛАБЫЙ: 'слабый сигнал',
  НЕТ_ПОДГОНКИ: 'подгонка не сошлась',
  ОТНОШЕНИЕ: 'B/A вне разумных границ',
  ГРАНИЦА: 'T1 на границе сетки',
});

const ПО_УМОЛЧАНИЮ = Object.freeze({
  минT1з: 20,        // границы сетки по T1* (кажущемуся), мс
  максT1з: 3500,
  узлов: 64,
  уточнений: 2,      // проходы дробления вокруг лучшего узла
  минТочек: 4,       // три параметра + знак: меньше пяти точек уже рискованно
  минСигнал: 0,      // абсолютный порог по наибольшему кадру (0 = не отсекать)
  минОтношение: 1.1, // B/A ≤ 1 даёт неположительный T1 — это не результат
  максОтношение: 3.5,
  полярность: 'окно',  // 'окно' | 'полный' | 'нет' (см. разбор ниже)
  окноПолярности: 2,   // сколько кадров по обе стороны от наименьшего модуля
});

/**
 * Подгонка A, B при фиксированном T1*.
 *
 * Система нормальных уравнений для S_i = A − B·f_i:
 *   n·A − Σf·B = ΣS
 *   Σf·A − Σf²·B = ΣSf
 * определитель равен −(n·Σf² − (Σf)²) и обращается в ноль только когда все f
 * одинаковы, то есть когда TI не различаются.
 *
 * @returns {{a: number, b: number, ошибка: number}|null}
 */
function подогнатьAB(s, f, n, сумF, сумFF) {
  let сумS = 0, сумSF = 0;
  for (let i = 0; i < n; i++) { сумS += s[i]; сумSF += s[i] * f[i]; }
  const D = n * сумFF - сумF * сумF;
  if (!(Math.abs(D) > 1e-12)) return null;
  const a = (сумS * сумFF - сумF * сумSF) / D;
  const b = (сумF * сумS - n * сумSF) / D;
  let ошибка = 0;
  for (let i = 0; i < n; i++) {
    const d = s[i] - (a - b * f[i]);
    ошибка += d * d;
  }
  return { a, b, ошибка };
}

/**
 * Подгонка одного воксельного набора: модель, полярность, поправка.
 *
 * @param {number[]} ti — времена инверсии, мс (по возрастанию не требуется: сортируется)
 * @param {ArrayLike<number>} модуль — измеренные значения (модуль сигнала)
 * @param {object} [нстр]
 * @returns {{ок: boolean, причина?: string, t1?: number, t1з?: number, a?: number, b?: number,
 *            отношение?: number, инвертировано?: number, r2?: number, невязка?: number}}
 *   t1з — кажущееся T1* (з от «зримое»): наружу отдаётся нарочно, чтобы разницу
 *   с поправленным T1 можно было увидеть, а не принимать на слово.
 */
export function подгонкаT1MOLLI(ti, модуль, нстр = {}) {
  const н = { ...ПО_УМОЛЧАНИЮ, ...нстр };
  const пары = [];
  for (let i = 0; i < ti.length; i++) {
    const t = Number(ti[i]);
    const m = Number(модуль[i]);
    if (!Number.isFinite(t) || t <= 0 || !Number.isFinite(m) || m < 0) continue;
    пары.push([t, m]);
  }
  if (пары.length < н.минТочек) {
    return { ок: false, причина: `для MOLLI нужно ${н.минТочек} кадра и более с разным TI, годных ${пары.length}` };
  }
  пары.sort((a, b) => a[0] - b[0]);
  const разныеTI = new Set(пары.map((п) => п[0]));
  if (разныеTI.size < 3) {
    return { ок: false, причина: 'кадры почти с одинаковым TI — три параметра по ним не определить' };
  }

  const n = пары.length;
  const T = new Float64Array(n);
  const M = new Float64Array(n);
  let макс = 0;
  for (let i = 0; i < n; i++) { T[i] = пары[i][0]; M[i] = пары[i][1]; if (M[i] > макс) макс = M[i]; }
  if (!(макс > 0) || макс < н.минСигнал) return { ок: false, причина: ПРИЧИНЫ.СЛАБЫЙ };

  // Сетка по T1*: логарифмическая — T1 тканей различается на порядок
  // (жир ~250 мс, кровь ~1600 мс), равномерная сетка тратила бы узлы не там.
  const узлов = Math.max(16, н.узлов | 0);
  const s = new Float64Array(n);
  const f = new Float64Array(n);

  // Сколько первых точек берём с минусом.
  //
  //   'окно'   — по умолчанию: k пробегает ОКНО вокруг кадра с наименьшим
  //              модулем. Точка нуля лежит рядом с этим кадром, но не обязана
  //              совпадать с ним (минимум по модулю лежит МЕЖДУ кадрами, а на
  //              шумных данных ближайший к нулю кадр может сдвинуться на один),
  //              поэтому окно, а не единственное значение. Замер: на 240 шумных
  //              прогонах (шум 5%, шесть значений T1) окно ±2 дало ТО ЖЕ k и тот
  //              же T1, что полный перебор, — расхождений ноль, разница 0,0000%,
  //              а кадр 128×128 считается 788 мс против 1021 мс, то есть на
  //              четверть быстрее. Числа закреплены в tests/molli_test.mjs.
  //   'полный' — перебрать все k. Медленнее; нужен как эталон для сверки окна.
  //   'нет'    — считать модуль как есть. Контрольный режим: он существует,
  //              чтобы КОНТРОЛЬ в тесте прошёл по ТОМУ ЖЕ коду и показал цену
  //              отказа от восстановления знака, а не по отдельной копии.
  let вариантыK;
  if (н.полярность === 'нет') {
    вариантыK = [0];
  } else if (н.полярность === 'полный') {
    вариантыK = Array.from({ length: n }, (_, k) => k);
  } else {
    let iМин = 0;
    for (let i = 1; i < n; i++) if (M[i] < M[iМин]) iМин = i;
    const ш = Math.max(1, н.окноПолярности | 0);
    const от = Math.max(0, iМин + 1 - ш);
    const до = Math.min(n - 1, iМин + 1 + ш);
    вариантыK = [];
    for (let k = от; k <= до; k++) вариантыK.push(k);
  }

  let лучшее = null;

  const прогон = (низ, верх, шагов) => {
    const логНиз = Math.log(Math.max(1, низ));
    const логВерх = Math.log(Math.max(низ * 1.0001, верх));
    const шаг = (логВерх - логНиз) / (шагов - 1);
    for (let g = 0; g < шагов; g++) {
      const t1з = Math.exp(логНиз + g * шаг);
      let сумF = 0, сумFF = 0;
      for (let i = 0; i < n; i++) { f[i] = Math.exp(-T[i] / t1з); сумF += f[i]; сумFF += f[i] * f[i]; }
      for (const k of вариантыK) {
        for (let i = 0; i < n; i++) s[i] = i < k ? -M[i] : M[i];
        const п = подогнатьAB(s, f, n, сумF, сумFF);
        if (!п) continue;
        if (!лучшее || п.ошибка < лучшее.ошибка) {
          лучшее = { ошибка: п.ошибка, t1з, a: п.a, b: п.b, k };
        }
      }
    }
  };

  прогон(н.минT1з, н.максT1з, узлов);
  if (!лучшее) return { ок: false, причина: ПРИЧИНЫ.НЕТ_ПОДГОНКИ };

  // Уточнение: дробим окно вокруг найденного узла. Полярность при этом
  // пересматривается тоже — на шумных данных лучший k меняется вместе с T1*.
  let окно = Math.exp((Math.log(н.максT1з) - Math.log(н.минT1з)) / (узлов - 1));
  for (let у = 0; у < (н.уточнений | 0); у++) {
    const центр = лучшее.t1з;
    прогон(центр / окно, центр * окно, 13);
    окно = Math.pow(окно, 1 / 6);
  }

  const { a, b, t1з, k, ошибка } = лучшее;
  if (!(a > 0) || !(b > 0)) return { ок: false, причина: ПРИЧИНЫ.НЕТ_ПОДГОНКИ };

  const отношение = b / a;
  if (!(отношение >= н.минОтношение) || !(отношение <= н.максОтношение)) {
    // B/A ≤ 1 означает T1 ≤ 0 после поправки — арифметически «результат» будет,
    // но он не про ткань. Такое возвращать нельзя даже с оговоркой.
    return { ок: false, причина: `${ПРИЧИНЫ.ОТНОШЕНИЕ}: B/A = ${отношение.toFixed(2)}`, t1з, a, b, отношение };
  }

  const t1 = t1з * (отношение - 1);
  if (!(t1 > 0) || !Number.isFinite(t1)) return { ок: false, причина: ПРИЧИНЫ.НЕТ_ПОДГОНКИ, t1з, a, b, отношение };

  // Ответ, упёршийся в границу сетки, — это не ответ, а край перебора.
  const наГранице = t1з <= н.минT1з * 1.02 || t1з >= н.максT1з * 0.98;
  if (наГранице) return { ок: false, причина: ПРИЧИНЫ.ГРАНИЦА, t1, t1з, a, b, отношение };

  // R² считается по ВОССТАНОВЛЕННОМУ сигналу (со знаком): по модулю он вышел бы
  // завышенным — отражённая ветка «объясняется» любой кривой заметно лучше.
  for (let i = 0; i < n; i++) s[i] = i < k ? -M[i] : M[i];
  let сред = 0;
  for (let i = 0; i < n; i++) сред += s[i];
  сред /= n;
  let полн = 0;
  for (let i = 0; i < n; i++) полн += (s[i] - сред) * (s[i] - сред);
  const r2 = полн > 0 ? Math.max(0, 1 - ошибка / полн) : 1;

  return { ок: true, t1, t1з, a, b, отношение, инвертировано: k, r2, невязка: Math.sqrt(ошибка / n) };
}

/**
 * Карта T1 по кадрам MOLLI.
 *
 * @param {Array<{ti: number, данные: ArrayLike<number>}>} кадры
 * @param {object} [нстр] — те же настройки, что у подгонкаT1MOLLI
 * @returns {{надёжно: boolean, причина: string|null, t1: Float32Array, t1з: Float32Array,
 *            отношение: Float32Array, r2: Float32Array, посчитано: number, отказов: number,
 *            отказыПоПричинам: Record<string, number>, ti: number[], модель: string}}
 */
export function картаT1MOLLI(кадры, нстр = {}) {
  const н = { ...ПО_УМОЛЧАНИЮ, ...нстр };
  const пусто = {
    t1: new Float32Array(0), t1з: new Float32Array(0), отношение: new Float32Array(0),
    r2: new Float32Array(0), посчитано: 0, отказов: 0, отказыПоПричинам: {}, ti: [],
    модель: 'MOLLI: A − B·exp(−TI/T1*), поправка T1 = T1*·(B/A − 1)',
  };
  if (!Array.isArray(кадры) || кадры.length < н.минТочек) {
    return { надёжно: false, причина: `для MOLLI нужно ${н.минТочек} кадра и более, дано ${кадры?.length || 0}`, ...пусто };
  }
  const длина = кадры[0]?.данные?.length;
  if (!длина) return { надёжно: false, причина: 'в первом кадре нет данных', ...пусто };
  for (const к of кадры) {
    if (!к?.данные || к.данные.length !== длина) {
      return { надёжно: false, причина: 'кадры разного размера', ...пусто };
    }
    if (!Number.isFinite(Number(к.ti)) || Number(к.ti) <= 0) {
      return { надёжно: false, причина: 'у кадра нет положительного TI', ...пусто };
    }
  }
  const ti = кадры.map((к) => Number(к.ti));
  if (new Set(ti).size < 3) {
    return { надёжно: false, причина: 'все кадры почти с одинаковым TI — три параметра не определить', ...пусто };
  }

  const N = длина;
  const t1 = new Float32Array(N).fill(NaN);
  const t1з = new Float32Array(N).fill(NaN);
  const отношение = new Float32Array(N).fill(NaN);
  const r2 = new Float32Array(N).fill(NaN);
  const отказыПоПричинам = {};
  const модуль = new Float64Array(кадры.length);
  let посчитано = 0, отказов = 0;

  for (let p = 0; p < N; p++) {
    for (let i = 0; i < кадры.length; i++) модуль[i] = кадры[i].данные[p];
    const и = подгонкаT1MOLLI(ti, модуль, н);
    if (!и.ок) {
      отказов++;
      const ключ = String(и.причина || 'без причины').split(':')[0];
      отказыПоПричинам[ключ] = (отказыПоПричинам[ключ] || 0) + 1;
      continue;
    }
    t1[p] = и.t1; t1з[p] = и.t1з; отношение[p] = и.отношение; r2[p] = и.r2;
    посчитано++;
  }

  return {
    надёжно: посчитано > 0,
    причина: посчитано > 0 ? null : 'ни один воксель не подошёл под модель MOLLI',
    t1, t1з, отношение, r2, посчитано, отказов, отказыПоПричинам, ti,
    модель: пусто.модель,
  };
}

/**
 * Похожа ли серия на MOLLI — по описанию. Нужно интерфейсу, чтобы выбрать
 * модель ДО расчёта, а не показать врачу заниженный T1 и промолчать.
 *
 * @param {string} описание
 * @returns {{molli: boolean, shmolli: boolean}}
 */
export function похожеНаMOLLI(описание = '') {
  const т = String(описание).toLowerCase();
  const shmolli = /shmolli|sh-molli/.test(т);
  return { molli: shmolli || /molli/.test(т), shmolli };
}

/** Оговорки, которые обязаны стоять рядом с числом. */
export function оговоркиMOLLI(описание = '') {
  const { shmolli } = похожеНаMOLLI(описание);
  const о = [
    'MOLLI зависит от частоты пульса: при тахикардии восстановление между инверсиями неполное, и T1 занижается. Поправка Лука–Локера этого не лечит.',
    'Коррекции движения нет: если дыхание не задержано, границы стенки смазаны, и среднее по ним неверно.',
    'TI берутся из тегов как есть. Если в серии записаны номинальные времена вместо эффективных, ошибка уйдёт прямо в ответ.',
  ];
  if (shmolli) {
    о.unshift('ShMOLLI требует условной подгонки (длинные T1 — по первым пяти кадрам). Здесь её нет: считается обычной трёхпараметрической моделью, и на длинных T1 ответ смещён.');
  }
  return о;
}

export default { картаT1MOLLI, подгонкаT1MOLLI, похожеНаMOLLI, оговоркиMOLLI, ПРИЧИНЫ };
